import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import { serverCache } from '../state/serverCache';

interface User {
  id: string;
  name: string;
  given_name: string;
  family_name: string;
  email: string;
  role: string;
  scope_code: string | null;
  is_active: boolean;
  apaar_id?: string;
  can_bulk: boolean;
  is_bulkgen_superadmin?: boolean;
  cache_scope?: string;
  force_password_change?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token?: string) => Promise<void>;
  logout: () => void;
  checkAuth: (showLoading?: boolean) => Promise<void>;
  refreshAfterMutation: (kind: AccountMutationKind) => Promise<void>;
  mutationRefresh: MutationRefreshState;
}

export type AccountMutationKind = 'email' | 'password';
type MutationRefreshState = {
  kind: AccountMutationKind;
  status: 'refreshing' | 'failed';
} | null;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

type AuthStatusResponse = {
  authenticated: boolean;
  user?: {
    id?: string;
    name?: string;
    given_name?: string;
    family_name?: string;
    email?: string;
    role?: string;
    scope_code?: string | null;
    is_active?: boolean;
    apaar_id?: string;
    can_bulk?: boolean;
    is_bulkgen_superadmin?: boolean;
    cache_scope?: string;
    force_password_change?: boolean;
  };
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mutationRefresh, setMutationRefresh] = useState<MutationRefreshState>(null);
  const isAuthenticated = !!user;
  const API_BASE = process.env.REACT_APP_API_BASE_URL || "/api";
  const pendingAuth = useRef<Promise<void> | null>(null);
  const authGeneration = useRef(0);

  const clearAuthentication = useCallback(() => {
    authGeneration.current += 1;
    pendingAuth.current = null;
    serverCache.clear();
    serverCache.setScope('anonymous');
    setMutationRefresh(null);
    setUser(null);
    setIsLoading(false);
  }, []);

  const checkAuth = useCallback((showLoading = true): Promise<void> => {
    if (pendingAuth.current) return pendingAuth.current;
    if (showLoading) setIsLoading(true);
    const generation = authGeneration.current;
    const operation = (async () => { try {
      const response = await fetch(`${API_BASE}/vedra/auth/status`, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(`Auth status check failed: ${response.status}`);
      }

      const payload = (await response.json()) as AuthStatusResponse;
      if (generation !== authGeneration.current) return;
      if (payload.authenticated && payload.user?.email) {
        // Older API versions may omit the fingerprint: allow volatile memory
        // reuse, but never restore/persist an unverifiable permission scope.
        const scope = payload.user.cache_scope
          ? `user:${payload.user.id}:${payload.user.cache_scope}`
          : `volatile:${payload.user.id}:${Date.now()}`;
        serverCache.setScope(scope);
        setMutationRefresh(null);
        setUser({
          id: payload.user.id || "",
          name: payload.user.name || payload.user.email,
          given_name: payload.user.given_name || "",
          family_name: payload.user.family_name || "",
          email: payload.user.email,
          role: payload.user.role || "",
          scope_code: payload.user.scope_code || null,
          is_active: payload.user.is_active ?? true,
          apaar_id: payload.user.apaar_id || "",
          can_bulk: payload.user.can_bulk ?? false,
          is_bulkgen_superadmin: payload.user.is_bulkgen_superadmin ?? false,
          cache_scope: payload.user.cache_scope,
          force_password_change: payload.user.force_password_change ?? false,
        });
      } else {
        serverCache.clear();
        serverCache.setScope('anonymous');
        setUser(null);
      }
    } catch (_error) {
      // Fail closed when auth endpoint is unavailable.
      if (generation === authGeneration.current) clearAuthentication();
    } finally {
      if (showLoading && generation === authGeneration.current) setIsLoading(false);
    } })();
    pendingAuth.current = operation;
    void operation.finally(() => { if (pendingAuth.current === operation) pendingAuth.current = null; });
    return operation;
  }, [API_BASE, clearAuthentication]);

  const refreshAfterMutation = useCallback(async (kind: AccountMutationKind) => {
    // A post-mutation read must not adopt a status request started before the
    // identity or credential change.
    authGeneration.current += 1;
    pendingAuth.current = null;
    const generation = authGeneration.current;
    setMutationRefresh({ kind, status: 'refreshing' });
    try {
      const response = await fetch(`${API_BASE}/vedra/auth/status`, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
      });
      if (generation !== authGeneration.current) return;
      if (response.status === 401 || response.status === 403) {
        clearAuthentication();
        return;
      }
      if (!response.ok) {
        throw new Error(`Auth status check failed: ${response.status}`);
      }
      const payload = (await response.json()) as AuthStatusResponse;
      if (generation !== authGeneration.current) return;
      if (!payload.authenticated || !payload.user?.email) {
        // A successful authoritative response says the session is no longer
        // authenticated. Preserve the normal fail-closed redirect behavior.
        clearAuthentication();
        return;
      }
      const scope = payload.user.cache_scope
        ? `user:${payload.user.id}:${payload.user.cache_scope}`
        : `volatile:${payload.user.id}:${Date.now()}`;
      serverCache.setScope(scope);
      setUser({
        id: payload.user.id || "",
        name: payload.user.name || payload.user.email,
        given_name: payload.user.given_name || "",
        family_name: payload.user.family_name || "",
        email: payload.user.email,
        role: payload.user.role || "",
        scope_code: payload.user.scope_code || null,
        is_active: payload.user.is_active ?? true,
        apaar_id: payload.user.apaar_id || "",
        can_bulk: payload.user.can_bulk ?? false,
        is_bulkgen_superadmin: payload.user.is_bulkgen_superadmin ?? false,
        cache_scope: payload.user.cache_scope,
        force_password_change: payload.user.force_password_change ?? false,
      });
      setMutationRefresh(null);
    } catch (error) {
      if (generation === authGeneration.current) {
        // The server did not authoritatively reject the session. Retain the
        // committed-mutation context, but let AccountRoute block private work
        // until a verified retry succeeds.
        setMutationRefresh({ kind, status: 'failed' });
      }
      throw error;
    }
  }, [API_BASE, clearAuthentication]);

  const login = useCallback(async (_token?: string) => {
    // A status read started before the login request cannot verify its result.
    authGeneration.current += 1;
    pendingAuth.current = null;
    await checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    clearAuthentication();
    try {
      // Call Invenio logout endpoint to clear session cookies
      await fetch(`/logout`, {
        method: "GET",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout API call failed:", error);
    }
    // Clear local state
    setUser(null);
    // Redirect to home page
    window.location.href = "/";
  }, [clearAuthentication]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    window.addEventListener('vedra:auth-invalid', clearAuthentication);
    return () => window.removeEventListener('vedra:auth-invalid', clearAuthentication);
  }, [clearAuthentication]);

  useEffect(() => {
    const refreshAuth = () => {
      void checkAuth(false);
    };
    const refreshVisibleAuth = () => {
      if (document.visibilityState === "visible") refreshAuth();
    };

    window.addEventListener("focus", refreshAuth);
    document.addEventListener("visibilitychange", refreshVisibleAuth);
    return () => {
      window.removeEventListener("focus", refreshAuth);
      document.removeEventListener("visibilitychange", refreshVisibleAuth);
    };
  }, [checkAuth]);

  const value: AuthContextType = useMemo(() => ({
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuth,
    refreshAfterMutation,
    mutationRefresh,
  }), [user, isAuthenticated, isLoading, login, logout, checkAuth, refreshAfterMutation, mutationRefresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
